import React from 'react';
import { ChevronRight, FileText, BookOpen, Target } from 'lucide-react';
import QuestionDisplay from './QuestionDisplay.tsx';
import InterviewCompletionScreen from './InterviewCompletionScreen.tsx';
import ProfileModal from './ProfileModal.tsx';

interface Question {
  id: string;
  question: string;
  answer?: string;
  isCollapsed?: boolean;
  field?: string;
  type?: string;
  questionNumber?: number;
  isCounterQuestion?: boolean;
}

interface InterviewResponse {
  session_id: string;
  interview_phase: string;
  project_details: {
    project_number: number | null;
    project_name: string | null;
    project_main_question_number: number | null;
    total_questions_planned_for_project: number | null;
  };
  is_interview_complete: boolean;
  main_question_number: number;
  main_question: string;
  counter_question_number: number | null;
  counter_question: string | null;
  total_questions_planned: number;
  reasoning_references: Array<{
    references: {
      file_name: string;
      page_number: number;
    };
    reasoning: string;
    gap_areas_focused: string;
  }>;
}

interface InterviewMainScreenProps {
  currentQuestion: Question | undefined;
  currentInterviewData: InterviewResponse | null;
  isFollowup: boolean;
  interviewComplete: boolean;
  allPhasesComplete: boolean;
  summary: string;
  summaryLoading: boolean;
  confirming: boolean;
  phase: string;
  sessionId: string | null;
  loadingNextQuestion: boolean;
  isLoadingQuestionWithAudio: boolean;
  isRecording: boolean;
  showDocumentUpload: boolean;
  uploadedDocuments: { [questionId: string]: File[] };
  elevenLabsError: string | null;
  elevenLabsIsPlaying: boolean;

  onAnswerChange: (questionId: string, answer: string) => void;
  onToggleRecording: () => void;
  onToggleDocumentUpload: () => void;
  onDocumentUpload: (questionId: string, file: File) => void;
  onDocumentRemove: (questionId: string, fileIndex: number) => void;
  onMuteToggle: () => void;

  onNextQuestion: () => void;
  onGenerateSummary: () => void;
  onSuccessScreenAction: (isFinal: boolean) => void;
  onSummaryGenerated: (summaryData: any, newPhase: string) => void;
  onReturningForInterview: () => void;
  setPhase: (phase: string) => void;
  setConfirming: (confirming: boolean) => void;
  formatPhase: (phaseString: string) => string;
  elevenLabs: any; // Assuming this is an ElevenLabs instance or similar
  profileData: any;
  profileSummary: any;
  fetchProfileSummary: () => void;
}

const InterviewMainScreen: React.FC<InterviewMainScreenProps> = ({
  currentQuestion,
  currentInterviewData,
  isFollowup,
  interviewComplete,
  allPhasesComplete,
  summary,
  summaryLoading,
  confirming,
  phase,
  sessionId,
  loadingNextQuestion,
  isLoadingQuestionWithAudio,
  isRecording,
  showDocumentUpload,
  uploadedDocuments,
  elevenLabsError,
  onAnswerChange,
  onToggleRecording,
  onToggleDocumentUpload,
  onDocumentUpload,
  onDocumentRemove,
  onNextQuestion,
  onGenerateSummary,
  onSuccessScreenAction,
  onSummaryGenerated,
  onReturningForInterview,
  setPhase,
  setConfirming,
  formatPhase,
  elevenLabsIsPlaying,
  onMuteToggle,
  elevenLabs,
  profileData,
  profileSummary,
  fetchProfileSummary
}) => {

  console.log('Current Interview Data:', profileSummary);
  // Calculate progress percentage - use counter_question_number if available, otherwise main_question_number
  const currentQuestionNumber =  currentInterviewData?.main_question_number || 0;
  const totalQuestions = currentInterviewData?.total_questions_planned || 1;
  const progressPercentage = Math.round((currentQuestionNumber / totalQuestions) * 100);

  // Determine question type for display
  const questionType = currentInterviewData?.counter_question ? "Follow-up Question" : "Main Question";
  const questionDisplayNumber =  currentInterviewData?.main_question_number || 0;
  const [showProfileModal, setShowProfileModal] = React.useState(false);

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            Interview Session
          </h1>

          {isFollowup && currentQuestion && (
            <div className="gap-4 flex">
              <span className='px-4 py-2 bg-[#bb1ccc] text-white rounded-lg'
               onClick={() => setShowProfileModal(true)}
              >View Profile</span>
              <span className="text-sm font-medium px-4 py-2 bg-purple-100 rounded-lg" style={{ color: '#bb1ccc' }}>
                {formatPhase(currentInterviewData?.interview_phase || "")}
              </span>
            </div>
          )}

        </div>

        

        {isFollowup && currentQuestion && (
          <>
          
     
        {/* Progress Bar Section */}
        {currentInterviewData && !currentInterviewData.is_interview_complete && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="text-lg font-semibold text-gray-700">
                  {questionType}
                </span>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  {questionDisplayNumber} of {totalQuestions}
                </span>
              </div>
              <span className="text-lg font-bold text-purple-600">
                {progressPercentage}%
              </span>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
              <div
                className="h-3 rounded-full transition-all duration-500 shadow-sm"
                style={{ 
                  width: `${progressPercentage}%`, 
                  background: "linear-gradient(90deg, #bb1ccc 0%, #d946ef 100%)" 
                }}
              ></div>
            </div>
            
            <div className="flex justify-between text-sm text-gray-500 mt-2">
              <span>Started</span>
              <span>In Progress</span>
              <span>Complete</span>
            </div>
          </div>
        )}
     </>
        )}
        {/* Reasoning References Section */}
       
      </div>

      {/* Show completion message when interview is complete */}
      {!isFollowup ? (
        <InterviewCompletionScreen
          allPhasesComplete={allPhasesComplete}
          summary={summary}
          summaryLoading={summaryLoading}
          confirming={confirming}
          phase={phase}
          sessionId={sessionId}
          onGenerateSummary={onGenerateSummary}
          onSuccessScreenAction={onSuccessScreenAction}
          onSummaryGenerated={onSummaryGenerated}
          onReturningForInterview={onReturningForInterview}
          setPhase={setPhase}
          setConfirming={setConfirming}
        />
      ) : currentQuestion && isFollowup ? (
        <>
          <QuestionDisplay
            currentQuestion={currentQuestion}
            isRecording={isRecording}
            showDocumentUpload={showDocumentUpload}
            uploadedDocuments={uploadedDocuments}
            elevenLabsError={elevenLabsError}
            onAnswerChange={onAnswerChange}
            onToggleRecording={onToggleRecording}
            onToggleDocumentUpload={onToggleDocumentUpload}
            onDocumentUpload={onDocumentUpload}
            onDocumentRemove={onDocumentRemove}
            elevenLabsIsPlaying={elevenLabsIsPlaying}
            onMuteToggle={onMuteToggle}
            elevenLabs={elevenLabs}
            onNextQuestion={onNextQuestion}
            loadingNextQuestion={loadingNextQuestion}
            isLoadingQuestionWithAudio={isLoadingQuestionWithAudio}
            isFollowup={isFollowup}
            interviewComplete={interviewComplete}
            onGenerateSummary={onGenerateSummary}
            />

{currentInterviewData?.reasoning_references && currentInterviewData.reasoning_references.length > 0 && !interviewComplete && (
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
            <div className="flex items-center gap-3 mb-4">
              <BookOpen className="w-6 h-6 text-blue-600" />
              <h3 className="text-lg font-semibold text-blue-900">
                Question Context & References
              </h3>
            </div>
            
            <div className="space-y-4">
              {currentInterviewData.reasoning_references.map((ref, index) => (
                <div key={index} className="bg-white rounded-lg p-4 border border-blue-100">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                    </div>
                    
                    <div className="flex-1 space-y-3">
                      {/* Reference Source */}
                      <div className="flex items-center gap-2 text-sm">
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span className="font-medium text-gray-700">
                          {ref.references.file_name}
                        </span>
                        <span className="text-gray-500">
                          (Page {ref.references.page_number})
                        </span>
                      </div>
                      
                      {/* Reasoning */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Reasoning:</h4>
                        <p className="text-gray-700 leading-relaxed">
                          {ref.reasoning}
                        </p>
                      </div>
                      
                      {/* Gap Areas Focused */}
                      <div className="flex items-start gap-2">
                        <Target className="w-4 h-4 text-purple-600 mt-1 flex-shrink-0" />
                        <div>
                          <span className="font-medium text-purple-900">Focus Areas: </span>
                          <span className="text-purple-700">
                            {ref.gap_areas_focused}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

          {/* Navigation */}
          <div className="flex items-center justify-between">

           
          </div>
        </>
      ) : (
        <div className="text-center py-16">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-gray-600 text-lg">Loading your first question...</p>
        </div>
      )}

       {/* Profile Modal */}
       <ProfileModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          profileData={profileData}
          profileSummary={profileSummary}
          fetchProfileSummary={fetchProfileSummary}
        />
    </div>
  );
};

export default InterviewMainScreen;